import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Atomic weights for common elements (IUPAC standard, close to CalcNet)
const AW = {
  H:1.008, He:4.0026, Li:6.94, Be:9.0122, B:10.81, C:12.011, N:14.007, O:15.999, F:18.998, Ne:20.180,
  Na:22.990, Mg:24.305, Al:26.982, Si:28.085, P:30.974, S:32.06, Cl:35.45, Ar:39.948, K:39.098, Ca:40.078,
  Sc:44.956, Ti:47.867, V:50.942, Cr:51.996, Mn:54.938, Fe:55.845, Co:58.933, Ni:58.693, Cu:63.546, Zn:65.38,
  Ga:69.723, Ge:72.630, As:74.922, Se:78.971, Br:79.904, Kr:83.798, Rb:85.468, Sr:87.62, Y:88.906, Zr:91.224,
  Nb:92.906, Mo:95.95, Tc:98, Ru:101.07, Rh:102.906, Pd:106.42, Ag:107.868, Cd:112.414, In:114.818, Sn:118.710,
  Sb:121.760, Te:127.60, I:126.904, Xe:131.293, Cs:132.905, Ba:137.327, La:138.905, Ce:140.116, Pr:140.908,
  Nd:144.242, Sm:150.36, Eu:151.964, Gd:157.25, Tb:158.925, Dy:162.500, Ho:164.930, Er:167.259, Tm:168.934,
  Yb:173.045, Lu:174.967, Hf:178.49, Ta:180.948, W:183.84, Re:186.207, Os:190.23, Ir:192.217, Pt:195.084,
  Au:196.967, Hg:200.592, Tl:204.38, Pb:207.2, Bi:208.980, U:238.029
};

function parseFormula(formula) {
  // Handles nested parentheses and multipliers
  const tokens = formula.replace(/\s+/g,'');
  let i = 0;
  function parseSeq() {
    const counts = {};
    while (i < tokens.length && tokens[i] !== ')') {
      if (tokens[i] === '(') {
        i++;
        const inner = parseSeq();
        if (tokens[i] !== ')') throw new Error('Unbalanced parentheses');
        i++;
        const mult = readNumber();
        for (const [el, c] of Object.entries(inner)) counts[el] = (counts[el] || 0) + c * mult;
      } else if (/[A-Z]/.test(tokens[i])) {
        let sym = tokens[i]; i++;
        while (i < tokens.length && /[a-z]/.test(tokens[i])) { sym += tokens[i]; i++; }
        const mult = readNumber();
        counts[sym] = (counts[sym] || 0) + mult;
      } else {
        throw new Error('Unexpected char: ' + tokens[i]);
      }
    }
    return counts;
  }
  function readNumber() {
    let n = ''; while (i < tokens.length && /[0-9]/.test(tokens[i])) { n += tokens[i]; i++; }
    return n ? parseInt(n, 10) : 1;
  }
  return parseSeq();
}

export default function MolecularWeightCalc() {
  const [formula, setFormula] = useState('C6H12O6');
  const [error, setError] = useState('');
  const PRESETS = [
    { f: 'H2O', name: 'Water' },
    { f: 'CO2', name: 'Carbon Dioxide' },
    { f: 'O2', name: 'Oxygen' },
    { f: 'N2', name: 'Nitrogen' },
    { f: 'NaCl', name: 'Sodium Chloride' },
    { f: 'C6H12O6', name: 'Glucose' },
    { f: 'C12H22O11', name: 'Sucrose' },
    { f: 'CH4', name: 'Methane' },
    { f: 'C2H6O', name: 'Ethanol' },
    { f: 'HCl', name: 'Hydrochloric Acid' },
    { f: 'H2SO4', name: 'Sulfuric Acid' },
    { f: 'HNO3', name: 'Nitric Acid' },
    { f: 'NaOH', name: 'Sodium Hydroxide' },
    { f: 'KOH', name: 'Potassium Hydroxide' },
    { f: 'NH3', name: 'Ammonia' },
    { f: 'Ca(OH)2', name: 'Calcium Hydroxide' },
    { f: 'CaCO3', name: 'Calcium Carbonate' },
    { f: 'Fe2O3', name: 'Iron(III) Oxide' },
    { f: 'CuSO4', name: 'Copper(II) Sulfate' },
    { f: 'C8H10N4O2', name: 'Caffeine' },
    { f: 'C9H8O4', name: 'Aspirin' },
    { f: 'C21H30O2', name: 'Cannabidiol (CBD)' },
    { f: 'CH3COOH', name: 'Acetic Acid' },
    { f: 'C6H5OH', name: 'Phenol' },
  ];
  const parsed = useMemo(() => {
    try { setError(''); return parseFormula(formula); }
    catch (e) { setError(e.message); return {}; }
  }, [formula]);

  const rows = Object.entries(parsed).map(([el, n]) => ({ el, n, aw: AW[el], contrib: (AW[el] || 0) * n }));
  const total = rows.reduce((a, r) => a + r.contrib, 0);
  const hasUnknown = rows.some(r => !r.aw);

  return (
    <CalcShell look="cnet" title="Molecular Weight Calculator" subtitle="Enter a chemical formula (e.g. H2O, C6H12O6, Ca(OH)2) to compute the molar mass.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Chemical Formula</label>
            <input value={formula} onChange={e => setFormula(e.target.value)} data-testid="mw-formula" placeholder="e.g. C6H12O6"/>
          </div>
          <div className="mt-2">
            <label className="text-xs text-slate-500 block mb-1">Or pick a common molecule:</label>
            <select value="" onChange={e => e.target.value && setFormula(e.target.value)}
              className="w-full px-3 py-2 rounded border border-slate-300 bg-white text-sm" data-testid="mw-preset">
              <option value="">— select a preset —</option>
              {PRESETS.map(p => <option key={p.f} value={p.f}>{p.name} ({p.f})</option>)}
            </select>
          </div>
          <p className="text-xs text-slate-500 mt-2">Case-sensitive. Use parentheses for groups (e.g. Ca(OH)2).</p>
          {error && <p className="text-sm text-rose-600 mt-2">{error}</p>}
          {hasUnknown && <p className="text-sm text-amber-600 mt-2">Some symbols are not recognised.</p>}
        </div>
        <div>
          <div className="p-5 rounded-xl bg-purple-50 border border-purple-200 text-center mb-4">
            <div className="text-sm text-slate-600">Molar Mass</div>
            <div className="text-4xl font-bold text-purple-700" data-testid="mw-result">{total.toFixed(4)} g/mol</div>
            <div className="text-xs text-slate-500 mt-2 font-mono">{formula}</div>
          </div>

          {/* Schematic composition visual — element bubbles sized by contribution */}
          {rows.length > 0 && !hasUnknown && (
            <div className="p-3 rounded-xl bg-white border border-slate-200 mb-4">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Composition</div>
              <svg viewBox={`0 0 ${Math.max(300, rows.length * 90)} 130`} className="w-full">
                {rows.map((r, i) => {
                  const cx = 50 + i * 90;
                  const pct = total ? (r.contrib / total) : 0;
                  const R = Math.max(18, 20 + pct * 30);
                  const colors = { C:'#1e293b', H:'#e2e8f0', O:'#dc2626', N:'#2563eb', S:'#eab308', P:'#f97316', Cl:'#22c55e', Na:'#a855f7', Ca:'#facc15', Fe:'#b45309' };
                  const fill = colors[r.el] || '#94a3b8';
                  const textFill = r.el === 'H' ? '#0f172a' : '#fff';
                  return (
                    <g key={i}>
                      {i < rows.length - 1 && <line x1={cx + R} y1="65" x2={cx + 90 - R} y2="65" stroke="#94a3b8" strokeWidth="2"/>}
                      <circle cx={cx} cy="65" r={R} fill={fill} stroke="#0f172a" strokeWidth="1.5"/>
                      <text x={cx} y="70" textAnchor="middle" fill={textFill} className="text-sm font-bold">{r.el}</text>
                      <text x={cx} y="105" textAnchor="middle" className="text-[10px]" fill="#475569">×{r.n}</text>
                      <text x={cx} y="118" textAnchor="middle" className="text-[10px]" fill="#94a3b8">{(pct * 100).toFixed(1)}%</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          )}

          <table className="data-table">
            <thead><tr><th>Element</th><th>Count</th><th>Atomic Weight</th><th className="text-right">Contribution</th></tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.el}><td>{r.el}</td><td>{r.n}</td><td>{r.aw ?? '—'}</td><td className="text-right">{r.contrib.toFixed(4)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </CalcShell>
  );
}
